import { useState } from "react";
import { formatCurrency, formatDateLong, maskAccountNumber } from "../../../utils/function";
import { getPayoutAvatarUrl, PayoutRequest, REJECTION_REASONS } from "./types";
import { Popover } from "../../../components/popover";

export const StatusBadge = ({ status }: { status: PayoutRequest["status"] }) => {
  const config: Record<PayoutRequest["status"], { bg: string; text: string; label: string }> = {
    approved: { bg: "bg-[#E6F9F1]", text: "text-[#00875A]", label: "Approved" },
    pending: { bg: "bg-[#FFF3E0]", text: "text-[#E65100]", label: "Pending" },
    rejected: { bg: "bg-[#FDECEA]", text: "text-[#C62828]", label: "Rejected" },
  };
  const c = config[status] || config.pending;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Period filtering now uses the shared PeriodDropdown (components/period-filter.tsx) — same
// Daily/Weekly/Monthly/All time/Custom Range picker used on the Recommenders and Admin
// Dashboard pages — instead of this page's own narrower Today/7d/30d dropdown.

export const PAYOUT_STATUS_OPTIONS: { value: "All" | "pending" | "approved" | "rejected"; label: string }[] = [
  { value: "All", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export const ActionMenu = ({
  onApprove,
  onReject,
}: {
  onApprove: () => void;
  onReject: () => void;
}) => {
  return (
    <Popover
      trigger={({ toggle }) => (
        <button
          onClick={toggle}
          className="text-gray-400 hover:text-gray-600 px-1 text-lg leading-none"
          aria-label="Payout actions"
        >
          •••
        </button>
      )}
      menuClassName="w-44 bg-white border border-gray-100 rounded-xl shadow-lg py-1"
    >
      {(close) => (
        <>
          <button
            onClick={() => {
              close();
              onApprove();
            }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
          >
            Approve Payout
          </button>
          <button
            onClick={() => {
                close();
                onReject();
              }}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
            >
              Reject Payout
            </button>
        </>
      )}
    </Popover>
  );
};

export const ApprovePayoutModal = ({
  payout,
  onConfirm,
  onClose,
  isSubmitting,
}: {
  payout: PayoutRequest;
  onConfirm: () => void;
  onClose: () => void;
  isSubmitting: boolean;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
      <h3 className="text-lg font-bold text-[#121212]">Approve Payout Request?</h3>
      <p className="text-sm text-gray-500 mt-1 mb-5">
        You're about to approve this payout request. Please review the details below before
        continuing.
      </p>

      <div className="bg-gray-50 rounded-2xl p-4">
        <div className="flex items-center gap-3 mb-4">
          <img
            src={payout.image_url || getPayoutAvatarUrl(payout)}
            alt={`${payout.user_first_name} ${payout.user_last_name}`}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
            onError={(e) => {
              (e.target as HTMLImageElement).src = getPayoutAvatarUrl(payout);
            }}
          />
          <span className="font-semibold text-[#333D4D]">{payout.user_first_name} {payout.user_last_name}</span>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-[#121212]">Payout Amount</span>
            <span className="font-semibold text-[#121212]">
              {formatCurrency(payout.amount, "NGN", "symbol")}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-[#121212]">Bank Details</span>
            <span className="text-[#65799A]">
              {payout.bank_name} {maskAccountNumber(payout.account_number)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-[#121212]">Requested On</span>
            <span className="text-[#65799A]">{formatDateLong(new Date(payout.requested_at))}</span>
          </div>
        </div>
      </div>

      <div className="flex gap-2 items-start bg-[#E6F9F1] rounded-xl px-4 py-3 mt-4 text-sm text-[#00875A]">
        <span className="mt-0.5">ℹ</span>
        <span>Once approved, the recommender will be notified after their payout has been processed.</span>
      </div>

      <button
        onClick={onConfirm}
        disabled={isSubmitting}
        className="w-full bg-black text-white py-3 rounded-full font-medium mt-5 disabled:opacity-60"
      >
        {isSubmitting ? "Approving..." : "Approve Payout"}
      </button>
      <button
        onClick={onClose}
        disabled={isSubmitting}
        className="w-full border border-gray-200 py-3 rounded-full font-medium mt-2"
      >
        Cancel
      </button>
    </div>
  </div>
);

export const RejectPayoutModal = ({
  onConfirm,
  onClose,
  isSubmitting,
}: {
  payout: PayoutRequest;
  onConfirm: (reason: string, notes: string) => void;
  onClose: () => void;
  isSubmitting: boolean;
}) => {
  const [selectedReason, setSelectedReason] = useState<string>(REJECTION_REASONS[0].value);
  const [notes, setNotes] = useState("");
  const [isEditingNotes, setIsEditingNotes] = useState(true);
  const MAX_NOTES = 500;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl relative">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500"
          aria-label="Close"
        >
          ×
        </button>

        <h3 className="text-lg font-bold text-[#121212] pr-8">Reason for Rejection</h3>
        <p className="text-sm text-gray-500 mt-1 mb-5">
          Choose the most appropriate reason for payout rejection and add any additional notes if
          needed.
        </p>

        <div className="space-y-2">
          {REJECTION_REASONS.map((reason) => (
            <label
              key={reason.value}
              className={`flex items-start gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${
                selectedReason === reason.value ? "border-[#00875A] bg-gray-50" : "border-gray-100 bg-gray-50"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  selectedReason === reason.value ? "border-[#00875A]" : "border-gray-300"
                }`}
              >
                {selectedReason === reason.value && <span className="w-2.5 h-2.5 rounded-full bg-[#00875A]" />}
              </span>
              <input
                type="radio"
                className="sr-only"
                checked={selectedReason === reason.value}
                onChange={() => setSelectedReason(reason.value)}
              />
              <span>
                <span className="font-semibold text-sm block">{reason.label}</span>
                <span className="text-xs text-gray-500">{reason.description}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold text-[#121212]">Add Additional Notes (optional)</span>
            {isEditingNotes && (
              <span className="text-xs text-gray-400">
                {notes.length}/{MAX_NOTES}
              </span>
            )}
          </div>

          {isEditingNotes ? (
            <div className="rounded-xl border border-gray-200 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100 p-3">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES))}
                placeholder="Type to add your notes"
                rows={3}
                className="w-full outline-none text-sm resize-none placeholder:text-gray-400"
              />
              <div className="flex justify-end">
                <button
                  onClick={() => setIsEditingNotes(false)}
                  className="text-sm font-semibold underline underline-offset-2 text-[#121212]"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 p-3">
              <p className="text-sm text-gray-700">{notes || "No notes added"}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => setIsEditingNotes(true)}
                  className="text-sm font-semibold underline underline-offset-2 text-[#121212]"
                >
                  Edit Note
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 items-start bg-[#E6F9F1] rounded-xl px-4 py-3 mt-4 text-sm text-[#00875A]">
          <span className="mt-0.5">ℹ</span>
          <span>This rejection reason and additional notes will be shared with the recommender</span>
        </div>

        <button
          onClick={() => onConfirm(selectedReason, notes)}
          disabled={isSubmitting}
          className="w-full bg-black text-white py-3 rounded-full font-medium mt-5 disabled:opacity-60"
        >
          {isSubmitting ? "Rejecting..." : "Reject Payout Request"}
        </button>
      </div>
    </div>
  );
};
